
import React, { memo } from 'react';
import SectionHeading from '../ui/SectionHeading';
import { formatText } from '@/utils/report';

interface NumberedListSectionProps {
  content: string;
  index: number;
}

/**
 * Extracts numbered list items from content
 */
function extractNumberedItems(content: string): string[] {
  if (!content) return [];
  
  // Split content into lines
  const lines = content.split('\n');
  
  // Match numbered list item pattern
  const numberedListPattern = /^\d+\.\s+/;
  
  // Filter out lines with numbered items and clean them
  return lines
    .filter(line => numberedListPattern.test(line.trim()))
    .map(line => line.trim().replace(numberedListPattern, '').trim());
}

/**
 * Extracts a title from a list section (if present)
 */
function extractListTitle(content: string): string | null {
  if (!content) return null;
  
  // Try to find a title before the numbered list items
  const lines = content.split('\n');
  const numberedListPattern = /^\d+\.\s+/;
  
  // Look for a non-empty line before the first numbered item
  for (let i = 0; i < lines.length; i++) {
    if (numberedListPattern.test(lines[i].trim())) {
      // If there's a title before the first numbered item, return it
      if (i > 0 && lines[i - 1].trim().length > 0) {
        const titleLine = lines[i - 1].trim();
        // Check if this is a heading and clean it if needed
        if (/^#+\s+/.test(titleLine)) {
          return titleLine.replace(/^#+\s+/, '');
        }
        return titleLine;
      }
      break;
    }
  }
  
  return null;
}

const NumberedListSection: React.FC<NumberedListSectionProps> = ({ content, index }) => {
  // Check if this section has text that indicates it might need special handling
  const sectionTitle = extractListTitle(content);
  const listItems = extractNumberedItems(content);
  
  if (listItems.length === 0) return null;
  
  // Check if this is a Key Industry Trends section - if so, use bullet points
  const isKeyIndustryTrends = 
    (sectionTitle && sectionTitle.toLowerCase().includes('key industry trends')) ||
    content.toLowerCase().includes('key industry trends');
  
  return (
    <div key={index} className="my-5">
      {sectionTitle && (
        <SectionHeading text={sectionTitle} level="h3" className="mb-3 text-base font-medium" />
      )}
      {isKeyIndustryTrends ? (
        // For Key Industry Trends, use bullet points instead of numbers
        <ul className="ml-5 list-disc text-base space-y-2">
          {listItems.map((item, idx) => (
            <li key={idx} className="pl-2 text-gray-300" dangerouslySetInnerHTML={{ __html: formatText(item) }} />
          ))}
        </ul>
      ) : (
        // For all other sections, use numbers as before
        <ol className="ml-5 list-decimal text-base space-y-2">
          {listItems.map((item, idx) => (
            <li key={idx} className="pl-2 text-gray-300" dangerouslySetInnerHTML={{ __html: formatText(item) }} />
          ))}
        </ol>
      )}
    </div>
  );
};

export default memo(NumberedListSection);
